import { useState, useMemo } from 'react';
import { Download, Loader2 } from 'lucide-react';
import api from '../services/api';
import PdfPreview from '../components/PdfPreview';
import ToolPageSections from '../components/ToolPageSections';

const LINE_TYPES = [
  { id: 'counter-clockwise-oval', name: 'Counter-clockwise Oval' },
  { id: 'clockwise-oval', name: 'Clockwise Oval' },
  { id: 'vertical-line', name: 'Vertical Line' },
  { id: 'horizontal-line', name: 'Horizontal Line' },
  { id: 'left-slant-line', name: 'Left Slant Line' },
  { id: 'right-slant-line', name: 'Right Slant Line' },
  { id: 'wavy-line', name: 'Wavy Line' },
  { id: 'upstroke-connector', name: 'Upstroke Connector' },
  { id: 'downstroke-connector', name: 'Downstroke Connector' }
];

const PAPER_SIZES = [
  { id: 'a4', name: 'A4 (210 × 297 mm)', width: 210, height: 297 },
  { id: 'letter', name: 'US Letter (8.5 × 11 in)', width: 215.9, height: 279.4 }
];

const LINE_SPACING_OPTIONS = [
  { id: 'tight', name: 'Tight' },
  { id: 'normal', name: 'Normal' },
  { id: 'loose', name: 'Loose' }
];

export default function LineTracingWorksheet() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Content settings
  const [lineType, setLineType] = useState('horizontal-line');
  const [lineStyle, setLineStyle] = useState('dotted');
  const [numberOfPages, setNumberOfPages] = useState(1);

  // Line styling
  const [lineColor, setLineColor] = useState('#D3D3D3');
  const [lineThickness, setLineThickness] = useState(2);
  const [lineOpacity, setLineOpacity] = useState(35);

  // Page layout
  const [paperSize, setPaperSize] = useState('letter');
  const [orientation, setOrientation] = useState('tall');
  const [lineSpacing, setLineSpacing] = useState('normal');

  // Advanced settings
  const [worksheetTitle, setWorksheetTitle] = useState('');
  const [showNameField, setShowNameField] = useState(true);
  const [nameFieldLabel, setNameFieldLabel] = useState('Name');
  const [showDateField, setShowDateField] = useState(true);
  const [dateFieldLabel, setDateFieldLabel] = useState('Date');

  const previewSettings = useMemo(() => ({
    lineType,
    lineStyle,
    numberOfPages,
    lineColor,
    lineThickness,
    lineOpacity,
    paperSize,
    orientation,
    lineSpacing,
    worksheetTitle,
    showNameField,
    nameFieldLabel,
    showDateField,
    dateFieldLabel
  }), [lineType, lineStyle, numberOfPages, lineColor, lineThickness, lineOpacity, paperSize, orientation, lineSpacing, worksheetTitle, showNameField, nameFieldLabel, showDateField, dateFieldLabel]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/worksheets/generate', {
        toolType: 'tracing-lines',
        lineType,
        lineStyle,
        numberOfPages,
        lineColor,
        lineThickness,
        lineOpacity,
        paperSize,
        orientation,
        lineSpacing,
        worksheetTitle,
        showNameField,
        nameFieldLabel,
        showDateField,
        dateFieldLabel
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bastion-line-tracing-worksheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to generate worksheet. Please try again.');
      console.error('Generate error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-8 bg-gradient-to-b from-gray-50/30 to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <nav className="text-sm text-gray-600 mb-4">
            <span>Home</span> &gt; <span>All Tools</span> &gt; <span>Tracing Worksheets</span> &gt; <span className="text-bastion-black">Line Tracing Worksheets</span>
          </nav>
          <h1 className="text-4xl font-bold text-bastion-black mb-4">
            Free Printable <span className="text-bastion-black">Line Tracing</span> Worksheets
          </h1>
          <p className="text-gray-700 max-w-3xl mx-auto mb-4">
            Master the foundation of beautiful handwriting at any age. Whether you're helping a child
            develop pre-writing skills, or an adult looking to fix shaky, inconsistent strokes—line tracing
            builds the <strong>muscle memory</strong> and <strong>pen control</strong> for confident, legible writing.
          </p>
          <p className="text-gray-600 mb-6">
            Customize line thickness, style (solid or dotted), and pattern type. Download your PDF
            instantly—no signup required.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Pen Control Training', 'Stroke Stability', 'All Ages Welcome', 'Instant PDF Download'].map((tag) => (
              <span key={tag} className="px-4 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Content Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Line Tracing Worksheet</h2>
              <p className="text-sm text-gray-600 mb-6">Create line tracing practice sheets for developing fine motor skills</p>

              <div className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Content</h3>
                  <p className="text-sm text-gray-600 mb-4">Configure line types and layout for your worksheet</p>

                  {/* Line Type */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line Type</label>
                    <select
                      value={lineType}
                      onChange={(e) => setLineType(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    >
                      {LINE_TYPES.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Line Style */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line Style</label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="lineStyle"
                          value="dotted"
                          checked={lineStyle === 'dotted'}
                          onChange={(e) => setLineStyle(e.target.value)}
                          className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                        />
                        <span className="text-sm text-gray-700">Dotted</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="lineStyle"
                          value="solid"
                          checked={lineStyle === 'solid'}
                          onChange={(e) => setLineStyle(e.target.value)}
                          className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                        />
                        <span className="text-sm text-gray-700">Solid</span>
                      </label>
                    </div>
                  </div>

                  {/* Number of Pages */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Pages</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={numberOfPages}
                      onChange={(e) => setNumberOfPages(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                    <p className="text-sm text-gray-600 mt-1">Number of pages to generate</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Styling Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Line Styling</h3>
              <p className="text-sm text-gray-600 mb-4">Customize the appearance of practice lines</p>

              {/* Line Color */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={lineColor}
                    onChange={(e) => setLineColor(e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={lineColor.toUpperCase()}
                    onChange={(e) => setLineColor(e.target.value)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  />
                </div>
              </div>

              {/* Line Thickness */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Thickness</label>
                <input
                  type="range"
                  min="0.25"
                  max="2"
                  step="0.25"
                  value={lineThickness}
                  onChange={(e) => setLineThickness(parseFloat(e.target.value))}
                  className="w-full accent-bastion-black"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0.25</span>
                  <span>0.5</span>
                  <span>0.75</span>
                  <span>1</span>
                  <span>1.25</span>
                  <span>1.5</span>
                  <span>1.75</span>
                  <span>2</span>
                </div>
              </div>

              {/* Line Opacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Opacity</label>
                <input
                  type="range"
                  min="20"
                  max="100"
                  step="5"
                  value={lineOpacity}
                  onChange={(e) => setLineOpacity(parseInt(e.target.value))}
                  className="w-full accent-bastion-black"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>20%</span>
                  <span>35%</span>
                  <span>50%</span>
                  <span>65%</span>
                  <span>80%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            {/* Page Layout Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Page Layout</h3>
              <p className="text-sm text-gray-600 mb-4">Configure paper size, orientation, and spacing</p>

              {/* Paper Size */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Paper Size</label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                >
                  {PAPER_SIZES.map((size) => (
                    <option key={size.id} value={size.id}>{size.name}</option>
                  ))}
                </select>
              </div>

              {/* Orientation */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Orientation</label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="orientation"
                      value="tall"
                      checked={orientation === 'tall'}
                      onChange={(e) => setOrientation(e.target.value)}
                      className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                    />
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      Tall
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="6" y="3" width="12" height="18" rx="1" />
                      </svg>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="orientation"
                      value="wide"
                      checked={orientation === 'wide'}
                      onChange={(e) => setOrientation(e.target.value)}
                      className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                    />
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      Wide
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="6" width="18" height="12" rx="1" />
                      </svg>
                    </span>
                  </label>
                </div>
              </div>

              {/* Line Spacing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Spacing</label>
                <select
                  value={lineSpacing}
                  onChange={(e) => setLineSpacing(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                >
                  {LINE_SPACING_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Advanced Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Advanced</h3>
              <p className="text-sm text-gray-600 mb-4">Additional worksheet settings</p>

              {/* Worksheet Title */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Worksheet Title</label>
                <input
                  type="text"
                  maxLength={50}
                  value={worksheetTitle}
                  onChange={(e) => setWorksheetTitle(e.target.value)}
                  placeholder="Enter a title (optional)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                />
                <p className="text-sm text-gray-600 mt-1">{worksheetTitle.length}/50 characters</p>
              </div>

              {/* Show Name Field */}
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">Show Name Field</label>
                <button
                  type="button"
                  onClick={() => setShowNameField(!showNameField)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showNameField ? 'bg-bastion-black' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showNameField ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {showNameField && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name Field Label</label>
                  <input
                    type="text"
                    value={nameFieldLabel}
                    onChange={(e) => setNameFieldLabel(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  />
                </div>
              )}

              {/* Show Date Field */}
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">Show Date Field</label>
                <button
                  type="button"
                  onClick={() => setShowDateField(!showDateField)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showDateField ? 'bg-bastion-black' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showDateField ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {showDateField && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Field Label</label>
                  <input
                    type="text"
                    value={dateFieldLabel}
                    onChange={(e) => setDateFieldLabel(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  />
                </div>
              )}
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-4 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Generate PDF
                </>
              )}
            </button>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
          </div>

          {/* Preview Panel */}
          <PdfPreview toolType="tracing-lines" settings={previewSettings} pageCount={numberOfPages} />
        </div>

        {/* AI Analysis CTA */}
        <div className="mt-12 bg-bastion-gray-100 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Want personalized feedback on your handwriting?
          </h2>
          <p className="text-gray-700 mb-6">
            Try our AI-powered handwriting analysis tool. Get instant feedback on legibility,
            consistency, spacing, and more to improve your writing skills.
          </p>
          <a
            href="/analysis"
            className="inline-flex items-center gap-2 px-6 py-3 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl transition-all"
          >
            Try AI Handwriting Analysis
            <span className="bg-white/30 text-xs px-2 py-0.5 rounded-full">Free first try</span>
          </a>
        </div>

        <ToolPageSections toolType="tracing-lines" />
      </div>
    </div>
  );
}
